/**
 * The canonical first-party customer analytics event catalog.
 *
 * This list is the contract. The analytics service rejects any event name absent
 * from it, and migration 0022 seeds exactly these rows into
 * analytics_event_definitions, so a typo cannot quietly pollute the stream and the
 * documented funnel and the measured funnel are the same object.
 *
 * `requiresConsent` is false only for events needed to operate the service safely
 * (for example a rejected overnight label, which is an integrity signal). Every
 * behavioural and marketing event requires analytics consent.
 */

import type { AnalyticsEventCategory, FunnelStep } from './customer.ts';

export interface AnalyticsEventDefinition {
  name: string;
  description: string;
  category: AnalyticsEventCategory;
  funnelStep: FunnelStep | null;
  requiresConsent: boolean;
  ownerPhase: number;
}

const ev = (
  name: string,
  description: string,
  category: AnalyticsEventCategory,
  funnelStep: FunnelStep | null = null,
  requiresConsent = true,
  ownerPhase = 3,
): AnalyticsEventDefinition => ({
  name,
  description,
  category,
  funnelStep,
  requiresConsent,
  ownerPhase,
});

export const ANALYTICS_EVENT_CATALOG: readonly AnalyticsEventDefinition[] = [
  // Discovery
  ev('homepage.viewed', 'Customer opened the marketplace homepage.', 'DISCOVERY', 'VISITOR'),
  ev(
    'location.selected',
    'Customer chose or changed their delivery location.',
    'DISCOVERY',
    'LOCATION',
  ),
  ev(
    'location.not_serviceable',
    'A chosen location resolved outside every live service area.',
    'DISCOVERY',
    'LOCATION',
  ),
  ev('category.viewed', 'Customer opened a category listing.', 'DISCOVERY', 'BROWSE'),
  ev('store.viewed', 'Customer opened a retailer storefront.', 'STORE', 'BROWSE'),
  ev('store.favorited', 'Customer favourited a store.', 'STORE'),
  ev('store.unfavorited', 'Customer removed a store favourite.', 'STORE'),

  // Search
  ev('search.submitted', 'Customer submitted a search query.', 'SEARCH', 'BROWSE'),
  ev('search.suggestion_selected', 'Customer picked a search suggestion.', 'SEARCH', 'BROWSE'),
  ev('search.zero_results', 'A search returned nothing, which is a demand signal.', 'SEARCH'),
  ev('search.filter_applied', 'Customer applied a search filter.', 'SEARCH'),
  ev('search.sort_changed', 'Customer changed the result ordering.', 'SEARCH'),
  ev(
    'overnight_filter.used',
    'Customer filtered results to overnight availability.',
    'AFTER_HOURS',
  ),

  // Product
  ev('product.viewed', 'Customer opened a product detail page.', 'PRODUCT', 'PRODUCT_VIEW'),
  ev('product.variant_selected', 'Customer selected a product variant.', 'PRODUCT', 'PRODUCT_VIEW'),
  ev('product.favorited', 'Customer favourited a product.', 'PRODUCT'),
  ev('product.unfavorited', 'Customer removed a product favourite.', 'PRODUCT'),
  ev('product.added_to_list', 'Customer added a product to a shopping list.', 'PRODUCT'),

  // Cart
  ev('cart.item_added', 'Customer added an item to the bag.', 'CART', 'ADD_TO_BAG'),
  ev('cart.item_removed', 'Customer removed an item from the bag.', 'CART'),
  ev('cart.quantity_changed', 'Customer changed a bag line quantity.', 'CART'),
  ev('cart.viewed', 'Customer opened the bag.', 'CART', 'ADD_TO_BAG'),
  ev(
    'cart.revalidated',
    'The bag was re-checked against live inventory and price.',
    'CART',
    null,
    false,
  ),
  ev(
    'cart.item_unavailable',
    'A bag line was found unavailable at revalidation.',
    'CART',
    null,
    false,
  ),

  // Beauty After Hours
  ev('beauty_after_hours.viewed', 'Customer opened the Beauty After Hours surface.', 'AFTER_HOURS'),
  ev(
    'beauty_after_hours.label_shown',
    'An overnight or late-night label passed every eligibility check and was rendered.',
    'AFTER_HOURS',
    null,
    false,
  ),
  ev(
    'beauty_after_hours.label_withheld',
    'An availability label was withheld because at least one condition failed. Recorded without consent because it evidences that no false promise was made.',
    'AFTER_HOURS',
    null,
    false,
  ),

  // Membership
  ev('beauty_burst_plus.viewed', 'Customer opened Beauty Burst+ discovery.', 'MEMBERSHIP'),
  ev(
    'beauty_burst_plus.interest_submitted',
    'Customer registered Beauty Burst+ interest.',
    'MEMBERSHIP',
    'ACCOUNT_OR_LEAD',
  ),

  // Leads
  ev('lead.submitted', 'Customer submitted a first-party lead form.', 'LEAD', 'ACCOUNT_OR_LEAD'),
  ev('retailer_interest.submitted', 'A store owner submitted the partnership form.', 'LEAD'),
  ev('waitlist.submitted', 'Customer joined a service-area waitlist.', 'LEAD', 'ACCOUNT_OR_LEAD'),

  // Account
  ev('signup.started', 'Customer began creating an account.', 'ACCOUNT', 'ACCOUNT_OR_LEAD'),
  ev('signup.completed', 'Customer finished creating an account.', 'ACCOUNT', 'ACCOUNT_OR_LEAD'),
  ev('signin.completed', 'Customer signed in.', 'ACCOUNT', null, false),
  ev('address.saved', 'Customer saved a delivery address.', 'ACCOUNT'),
  ev(
    'notification_preferences.updated',
    'Customer changed notification preferences.',
    'ACCOUNT',
    null,
    false,
  ),

  // Funnel steps owned by a later phase. Declared so the funnel definition is
  // complete; they cannot fire in Phase 3 because no checkout exists.
  ev(
    'checkout.started',
    'Customer began checkout. Owned by Phase 4.',
    'FUNNEL',
    'CHECKOUT',
    true,
    4,
  ),
  ev(
    'order.placed',
    'Customer completed a purchase. Owned by Phase 4.',
    'FUNNEL',
    'PURCHASE',
    true,
    4,
  ),
  // Fulfilment completion is recordable at Launch 1.0 (courier DELIVERED),
  // but the emission wiring lives in the delivery-orchestration follow-up.
  ev(
    'delivery.completed',
    'A fulfilment reached the courier DELIVERED transition and the order was handed to the customer.',
    'FUNNEL',
    null,
    true,
    6,
  ),
  // Checkout abandonment is a Phase-6 funnel drop-off. Declared so the funnel
  // definition is complete; emission requires the lifecycle scheduler
  // (checkout.abandoned_reminder), which is a separate effort.
  ev(
    'checkout.abandoned',
    'A customer started checkout but did not complete the purchase within the abandonment window.',
    'FUNNEL',
    'CHECKOUT',
    true,
    6,
  ),
  ev(
    'customer.repeat_purchase',
    'A returning customer purchased again. Owned by Phase 8.',
    'FUNNEL',
    'REPEAT',
    true,
    8,
  ),
  // Professional verification (Phase 8). Declared so the funnel + credential
  // lifecycle contracts are complete; these cannot fire until the
  // marketplace.beauty_professionals module is built and the owner has decided
  // accepted license types, jurisdictions, and which products are pro-only.
  // They are seeded inactive (ownerPhase 8 > MAX_EMITTABLE_EVENT_PHASE).
  ev(
    'credential.started',
    'A customer began submitting a professional license for verification.',
    'ACCOUNT',
    null,
    true,
    8,
  ),
  ev(
    'credential.verified',
    'A submitted professional license was verified and the customer unlocked professional-only products.',
    'ACCOUNT',
    null,
    true,
    8,
  ),
  ev(
    'professional_product.blocked',
    'A professional-only product was blocked because the customer lacked a verified professional credential. Recorded without consent as an integrity signal.',
    'PRODUCT',
    null,
    false,
    8,
  ),
];

export const ANALYTICS_EVENT_NAMES: readonly string[] = ANALYTICS_EVENT_CATALOG.map((e) => e.name);

/**
 * Highest phase whose events are actually implemented and therefore emittable.
 * Checkout and ordering are live at Launch 1.0, so their funnel events must be
 * recordable; genuinely unbuilt phases (loyalty, repeat-purchase analytics) stay
 * rejected so the funnel cannot be populated with events nothing can produce.
 */
export const MAX_EMITTABLE_EVENT_PHASE = 6;

/** Events the platform is allowed to emit. Later-phase definitions are rejected. */
export const EMITTABLE_EVENT_NAMES: readonly string[] = ANALYTICS_EVENT_CATALOG.filter(
  (e) => e.ownerPhase <= MAX_EMITTABLE_EVENT_PHASE,
).map((e) => e.name);

export function findAnalyticsEvent(name: string): AnalyticsEventDefinition | undefined {
  return ANALYTICS_EVENT_CATALOG.find((e) => e.name === name);
}
